import { useEffect, useState, useCallback } from "react"
import { Select, Space, Typography, Alert } from "antd"
import { filterOption } from "modules/utilities/utility"

const { Text } = Typography

const MedicalFlowQuestions = ({ flowQuestions, setValidation, setTicketPayload, onFlowBlockedChange }) => {
    const [answers, setAnswers] = useState([])

    const questionsMap = {}
    flowQuestions.forEach((q) => { questionsMap[q.id] = q })

    // On mount: block ticket creation until flow is complete
    useEffect(() => {
        setValidation((prev) => ({ ...prev, medical_flow: false }))

        return () => {
            // Cleanup on unmount: remove validation key and payload key
            setValidation((prev) => {
                const { medical_flow, ...rest } = prev
                return rest
            })
            setTicketPayload((prev) => {
                const { category_flow_selection, ...rest } = prev
                return rest
            })
            onFlowBlockedChange?.(false)
        }
    }, [])

    // Reset answers when flowQuestions change (subcategory changed)
    useEffect(() => {
        setAnswers([])
        setValidation((prev) => ({ ...prev, medical_flow: false }))
        setTicketPayload((prev) => {
            const { category_flow_selection, ...rest } = prev
            return rest
        })
        onFlowBlockedChange?.(false)
    }, [flowQuestions])

    // Build the visible question chain based on current answers
    const getVisibleQuestions = useCallback(() => {
        const visible = []
        if (!flowQuestions || flowQuestions.length === 0) return visible

        // First question is always the first in the array
        const firstQuestion = flowQuestions[0]
        if (!firstQuestion) return visible

        visible.push(firstQuestion)

        // Follow the chain based on answers
        for (let i = 0; i < answers.length; i++) {
            const answer = answers[i]
            const question = questionsMap[answer.question_id]
            if (!question) break

            const selectedOption = question.options.find((o) => o.value === answer.answer)
            if (!selectedOption || !selectedOption.next_question_id) break

            const nextQuestion = questionsMap[selectedOption.next_question_id]
            if (nextQuestion) visible.push(nextQuestion)
        }

        return visible
    }, [answers, flowQuestions])

    const visibleQuestions = getVisibleQuestions()

    // Determine the terminal state (last answered question's selected option)
    const getTerminalState = useCallback(() => {
        if (answers.length === 0) return null

        const lastAnswer = answers[answers.length - 1]
        const question = questionsMap[lastAnswer.question_id]
        if (!question) return null

        const selectedOption = question.options.find((o) => o.value === lastAnswer.answer)
        if (!selectedOption) return null

        // Terminal node: next_question_id is null
        if (selectedOption.next_question_id === null) {
            return {
                canRaiseTicket: selectedOption.can_raise_ticket,
                coachMessage: selectedOption.coach_message || null,
            }
        }

        return null
    }, [answers])

    const terminalState = getTerminalState()

    // Update validation and payload when terminal state changes
    useEffect(() => {
        if (terminalState) {
            const canRaise = terminalState.canRaiseTicket === true
            setValidation((prev) => ({ ...prev, medical_flow: canRaise }))
            onFlowBlockedChange?.(!canRaise)

            setTicketPayload((prev) => ({
                ...prev,
                category_flow_selection: {
                    answers: answers.map((a) => ({
                        question_id: a.question_id,
                        question: a.question,
                        answer: a.answer,
                        answer_label: a.answer_label,
                    })),
                    outcome: canRaise ? "raise_ticket" : "blocked",
                    coach_message: terminalState.coachMessage,
                },
            }))
        } else {
            // Not at terminal yet - keep blocked
            setValidation((prev) => ({ ...prev, medical_flow: false }))
            onFlowBlockedChange?.(false)
            setTicketPayload((prev) => {
                const { category_flow_selection, ...rest } = prev
                return rest
            })
        }
    }, [terminalState, answers])

    const onAnswerChange = (questionId, value) => {
        const question = questionsMap[questionId]
        if (!question) return

        const selectedOption = question.options.find((o) => o.value === value)

        // Find the index of this question in current answers
        const existingIndex = answers.findIndex((a) => a.question_id === questionId)

        let newAnswers
        if (existingIndex >= 0) {
            // Truncate: keep answers up to this question, replace current
            newAnswers = answers.slice(0, existingIndex)
        } else {
            newAnswers = [...answers]
        }

        if (value) {
            newAnswers.push({
                question_id: questionId,
                question: question.question,
                answer: value,
                answer_label: selectedOption?.label || value,
            })
        }

        setAnswers(newAnswers)
    }

    return (
        <div className="my-3">
            {visibleQuestions.map((question, index) => {
                const currentAnswer = answers.find((a) => a.question_id === question.id)
                return (
                    <Space.Compact block className="my-3" key={question.id}>
                        <span
                            style={{ background: "#FAFAFA", border: "#D9D9D9", width: "30%" }}
                            className="border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2"
                        >
                            <Text>{question.question}</Text>
                        </span>
                        <Select
                            style={{ width: "70%" }}
                            showSearch
                            allowClear
                            placeholder="Select an option"
                            optionFilterProp="children"
                            value={currentAnswer?.answer || undefined}
                            onChange={(value) => onAnswerChange(question.id, value)}
                            filterOption={filterOption}
                            options={question.options.map((opt) => ({
                                label: opt.label,
                                value: opt.value,
                            }))}
                        />
                    </Space.Compact>
                )
            })}

            {terminalState && terminalState.canRaiseTicket === true && (
                <Alert
                    type="success"
                    message="Raise Ticket"
                    description={terminalState.coachMessage}
                    showIcon
                    className="my-2"
                />
            )}

            {terminalState && terminalState.canRaiseTicket === false && (
                <Alert
                    type="error"
                    message="No Need to Raise Ticket"
                    description={terminalState.coachMessage}
                    showIcon
                    className="my-2"
                />
            )}
        </div>
    )
}

export default MedicalFlowQuestions
